import React, { useRef } from 'react';
import { Download, QrCode, Upload, X, AlertCircle, Printer, Camera } from 'lucide-react';

interface BoxLabel {
    id: number;
    index: number;
    code: string;
    png_url: string;
    pdf_url: string;
}

interface PickupLabelsStepProps {
    boxes: BoxLabel[];
    mergedPdfUrl: string | null;
    noPrinter: boolean;
    onNoPrinterChange: (v: boolean) => void;
    photos: File[];
    onPhotosChange: (files: File[]) => void;
    pickupType: 'quick' | 'detailed' | null;
}

const PickupLabelsStep: React.FC<PickupLabelsStepProps> = ({
    boxes,
    mergedPdfUrl,
    noPrinter,
    onNoPrinterChange,
    photos,
    onPhotosChange,
    pickupType,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const galleryRef = useRef<HTMLInputElement>(null);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isDetailed = pickupType === 'detailed';

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        onPhotosChange([...photos, ...files].slice(0, 10));
        if (inputRef.current) inputRef.current.value = '';
    };

    const removePhoto = (idx: number) => {
        onPhotosChange(photos.filter((_, i) => i !== idx));
    };

    return (
        <div className="space-y-6">
            {/* Labels */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                        Box Labels
                    </label>
                    {mergedPdfUrl && boxes.length > 1 && (
                        <a
                            href={mergedPdfUrl}
                            download
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download all (PDF)
                        </a>
                    )}
                </div>
                <div className="space-y-2">
                    {boxes.map(box => (
                        <div key={box.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded">
                                    <QrCode className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{box.code}</p>
                                        <p className="text-xs text-gray-500">
                                            Box {box.index}
                                            {isDetailed && ` — apply this label to box ${box.index}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <a
                                    href={box.png_url}
                                    download
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    PNG
                                </a>
                                <a
                                    href={box.pdf_url}
                                    download
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    PDF
                                </a>
                            </div>
                        </div>
                    ))}
                </div>

                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={noPrinter}
                        onChange={(e) => onNoPrinterChange(e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Printer className="h-3.5 w-3.5" />
                        I don't have a printer — I'll write the codes on each box
                    </span>
                </label>

                {noPrinter && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 space-y-1">
                        <p className="font-medium">Writing instructions</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            <li>Write each box code in large, clear letters on the top of the box</li>
                            <li>Use a permanent marker</li>
                            <li>Make sure the code is visible from outside the package</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Photos */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photos of Packages <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                    Upload at least one photo showing your labeled packages ready for pickup.
                    Make sure the codes are visible so we can verify each box.
                </p>

                <div className="grid grid-cols-4 gap-2 mb-3">
                    {photos.map((file, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                            <img src={URL.createObjectURL(file)} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                                type="button"
                                onClick={() => removePhoto(idx)}
                                className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}

                    {photos.length < 10 && isMobile && (
                        <>
                            <button
                                type="button"
                                onClick={() => cameraRef.current?.click()}
                                className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            >
                                <Camera className="h-5 w-5 text-gray-400" />
                                <span className="text-xs text-gray-500">Take Photo</span>
                            </button>
                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple onChange={handleFileSelect} className="hidden" />
                        </>
                    )}

                    {photos.length < 10 && (
                        <>
                            <button
                                type="button"
                                onClick={() => galleryRef.current?.click()}
                                className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            >
                                <Upload className="h-5 w-5 text-gray-400" />
                                <span className="text-xs text-gray-500">Choose File</span>
                            </button>
                            <input ref={galleryRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
                        </>
                    )}
                </div>

                {photos.length === 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        At least one photo is required
                    </p>
                )}
            </div>
        </div>
    );
};

export default PickupLabelsStep;